import type { Params } from '@jsonfmt/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { CommandContext, UiPort } from '@/features/types';
import { useServices } from '@/services';
import { useMessageCatalog } from '@/ui/i18n/messageCatalog';

/**
 * `CommandContext` assembled from the providers — docs/ARCHITECTURE.md §3, §4.9, ADR-0014.
 *
 * Everything a command is allowed to touch, and nothing else. The DOCUMENT is deliberately
 * absent: it arrives as the second argument of `run()`, which is what keeps commands unbound
 * to one document and makes a later Diff View `runCommand(cmd, ctx, storeB)` rather than a
 * rewrite of every command.
 *
 * `ui` is a PARAMETER rather than another hook read. The `UiPort` is built by `App.tsx` out of
 * the editor handle sink and its own dialog state — it is the composition root's own object,
 * not a provider's — and passing it in keeps this hook honest about where it comes from.
 */
export function useCommandContext(ui: UiPort): CommandContext {
  const services = useServices();
  const catalog = useMessageCatalog();
  const t = useTranslate();

  // `ui` is rebuilt per render by `App` unless it memoises; it does, so this memo is real.
  return useMemo<CommandContext>(() => ({ services, ui, t, catalog }), [services, ui, t, catalog]);
}

/** The signature `FeatureApi.t` and `CommandContext.t` both declare. */
export type Translate = (key: string, params?: Params) => string;

/**
 * `t`, widened from i18next's key-typed signature to the untyped one the feature layer speaks.
 *
 * The widening is a real loss and it is worth naming. `src/locales/types.d.ts` augments
 * i18next so a misspelled key is a compile error with a "Did you mean" suggestion — but
 * `Command.labelKey` and `Notice.key` are typed `string` in `src/features/types.ts`, because
 * that file is compiled by `tsconfig.core.json` (no DOM, no i18next) and cannot reach the key
 * union. So a key that travels through a `Notice` is checked by `npm run i18n:check`, not by
 * the compiler, and this assertion is where the two regimes meet.
 *
 * It is ONE cast in ONE place rather than a cast at every call site, which is the only reason
 * it is acceptable. Every component that renders its own strings uses `useTranslation()`
 * directly and keeps the typed keys.
 */
export function useTranslate(): Translate {
  const { t } = useTranslation();
  return useMemo<Translate>(() => t as unknown as Translate, [t]);
}
